import { FeatureCard2_3Paragraph } from "@/__generated__/graphql";
import { getLanguage } from "@/lib/config";
import {
  StyledFeatureCardVariantAnchorWrapper,
  StyledFeatureCardVariantGradientBox,
  StyledFeatureCardVariantGridContainer,
  StyledFeatureCardVariantPrimary,
  StyledFeatureCardVariantSecondary,
  StyledFeatureCardVariantWrapper,
  StyledPrimaryButton,
  StyledSecondaryButton
} from "@components/FeatureCardVariant/styles";

import { useMobile } from "@lib/hooks/useMobile";
import { useTablet } from "@lib/hooks/useTablet";
import { breakpoints, imageLoaderResponsive } from "@theme/lib";
import Image from 'next/image';
import { useState } from "react";
import { useTheme } from "styled-components";
import { bicentenaryBackgroundClass } from "@components/BicentenaryPatterns/util";
import { Maybe } from 'graphql/jsutils/Maybe';

interface ButtonLink {
  label: string;
  url: string;
  target: Maybe<string> | null;
}

interface ImageProps {
  alt?: string;
  url: string;
  sm: string;
  md: string;
  lg: string;
  xl: string;
  id: string;
}

interface Card {
  eyebrow: string | null;
  title: string;
  content: string | null;
  image: ImageProps;
  primaryBtn: ButtonLink | null;
  secondaryBtn: ButtonLink | null;
}

interface FeatureCardTwoOrThreeSectionProps {
  title: string;
  cards: Card[];
  colourTheme: string;
  __typename: string;
}

export function preprocessFeatureCardVariantData(data: FeatureCard2_3Paragraph): FeatureCardTwoOrThreeSectionProps {
  return {
    cards: data.singleFeatureCards.map((c) => {
      const tempCard = c.cards[0];
      return {
        eyebrow: tempCard.teaserEyebrow || "",
        title: tempCard.title || "",
        content: tempCard.teaserDescription || "",
        image: tempCard.teaserImage,
        primaryBtn: {
          label: c.primaryCtaLabel,
          url: tempCard.path,
          target: tempCard.target,
        },
        secondaryBtn: c.secondaryCta ? {
          label: c.secondaryCta.title,
          url: c.secondaryCta.url,
          target: c.secondaryCta.target,
        } : null
      }
    }),
    title: data.featureCardTitle,
    colourTheme: data.colourTheme,
    __typename: data.__typename
  }
}

export default function FeatureCardVariant(paragraph: FeatureCard2_3Paragraph) {
  const props = preprocessFeatureCardVariantData(paragraph);
  const isMobile = useMobile();
  const isTablet = useTablet();
  const { key } = useTheme();
  const language = getLanguage();
  const [isThreeSection, setIsThreeSection] = useState(() => props.cards.length > 2);

  if (!props?.cards?.length || props?.cards?.length < 0) {
    return <></>;
  }

  const bgColor = props.colourTheme;

  const getWidthForImage = (isPrimary?: boolean) => {
    if (isPrimary) {
      return isMobile ? 360 : (isTablet ? 1024 : 768);
    } else {
      if (isThreeSection) {
        return isMobile ? 324 : (isTablet ? 324 : 436)
      } else {
        return isMobile ? 324 : (isTablet ? 324 : 592)
      }
    }
  }
  const getHeightForImage = (isPrimary?: boolean) => {
    if (isPrimary) {
      return isMobile ? 465 : (isTablet ? 768 : 1024);
    } else {
      if (isThreeSection) {
        return isMobile ? 191 : (isTablet ? 191 : 381)
      } else {
        return isMobile ? 191 : (isTablet ? 191 : 381)
      }
    }
  }

  return (
    <StyledFeatureCardVariantWrapper colourTheme={bgColor} className={`${props.__typename} ${bicentenaryBackgroundClass(paragraph)}`}>
      {(bgColor !== 'black' && key === 'london') && (
        <>
          <img loading="lazy" alt="" className="pattern pattern-texture-top-right" src="/PatternTextureE.svg" />
          <img loading="lazy" alt="" className="pattern pattern-texture-bottom-left" src="/PatternTextureE.svg" />
        </>
      )}
      {(bgColor !== 'black' && key === 'whipsnade') && (
        <>
          <img loading="lazy" alt="" className="pattern pattern-texture-top-right" src="/PatternTextureC.svg" />
          <img loading="lazy" alt="" className="pattern pattern-texture-bottom-left" src="/PatternTextureC.svg" />
        </>
      )}
      <StyledFeatureCardVariantGridContainer
        className={`theme--${bgColor}`}
        colourTheme={bgColor}
        variantType={props.cards.length > 2 ? "three-section" : "two-section"}>
        <h2 lang={language !== 'en' ? language : undefined}>{props.title}</h2>
        {props.cards.map((card, i) => {
          return (i === 0) ? (
              <StyledFeatureCardVariantPrimary key={i} className={`feature-card-variant feature-card-variant__primary`}>
                <Image
                  src={card.image.url}
                  alt={card.image.alt || ''}
                  fill
                  style={{ objectFit: 'cover', objectPosition: 'center' }}
                  sizes={`(min-width: ${breakpoints.lg}px) 1024px,
                          (min-width: ${breakpoints.md}px) 768px,
                          360px`}
                  loader={imageLoaderResponsive(getWidthForImage(true), getHeightForImage(true))}
                />
                <StyledFeatureCardVariantGradientBox />
                <FeatureCardContent {...card} colourTheme={bgColor} theme={key}/>
              </StyledFeatureCardVariantPrimary>
            ) :
            (
              <StyledFeatureCardVariantSecondary key={i} className={`feature-card-variant feature-card-variant__secondary`}>
                <Image
                  src={card.image.url}
                  alt={card.image.alt || ''}
                  style={{ width: '100%', height: 'auto', objectFit: 'cover' }}
                  sizes={`(min-width: ${breakpoints.lg}px) 592px,
                          324px`}
                  width={getWidthForImage()}
                  height={getHeightForImage()}
                  loader={imageLoaderResponsive(getWidthForImage(), getHeightForImage())}
                />
                <FeatureCardContent {...card} colourTheme={bgColor} theme={key} />
              </StyledFeatureCardVariantSecondary>
            )
        })
        }
      </StyledFeatureCardVariantGridContainer>
    </StyledFeatureCardVariantWrapper>
  );
}

type ThemedCardProps = Card & {colourTheme: string, theme: string}

function FeatureCardContent(card: ThemedCardProps) {
  const language = getLanguage();
  return (
    <div className="feature-card-variant__content">
      {card.eyebrow && (
        <span
          dangerouslySetInnerHTML={{ __html: card.eyebrow }}
          lang={language !== 'en' ? language : undefined}
        />
      )}
      <h3 lang={language !== 'en' ? language : undefined}>{card.title}</h3>
      <p lang={language !== 'en' ? language : undefined}>{card.content}</p>
      <StyledFeatureCardVariantAnchorWrapper>
        {card.primaryBtn && (
          <StyledPrimaryButton
            className={`theme--${card.colourTheme} theme--${card.theme}`}
            href={card.primaryBtn?.url}
            lang={language !== 'en' ? language : undefined}
            target={card.primaryBtn?.target || ""}
          >
            {card.primaryBtn?.label}
          </StyledPrimaryButton>
        )}
        {card.secondaryBtn && (
          <StyledSecondaryButton
            className={`theme--${card.colourTheme} theme--${card.theme}`}
            href={card.secondaryBtn?.url}
            lang={language !== 'en' ? language : undefined}
            target={card.secondaryBtn?.target || ""}
          >
            {card.secondaryBtn?.label}
          </StyledSecondaryButton>
        )}
      </StyledFeatureCardVariantAnchorWrapper>
    </div>
  );
}
